import { Check } from 'lucide-react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { useOnboardingContext } from '@/features/onboarding-v2/context/OnboardingContext';
import { TypographyCaption } from '@heidi/design-system/typography';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';

import { LearnBasicsEvidenceQuestions } from '@heidi/growth';
import { useLearnBasicsTaskActions } from '../useLearnBasicsTaskActions';
import { tourStepsMessages } from './tour-steps.messages';
import { TourStepShell } from './TourStepShell';

interface TourEvidenceStepProps {
  onContinue: () => void;
  onSkip: () => void;
}

/**
 * Guided-tour screen 5: the Ask Heidi / evidence task, promoted to its own
 * screen. Asking a question opens the answer in the preview's Evidence tab.
 */
export function TourEvidenceStep({ onContinue, onSkip }: TourEvidenceStepProps) {
  const { micTest } = useOnboardingContext();
  const { evidenceQuestions, askEvidence, isNoteGenerating } =
    useLearnBasicsTaskActions();

  // Open the Evidence tab on entry. It exists for the whole screen — empty
  // chat until a question is asked, then the answer — so this both previews
  // where the answer will land and re-focuses it on the way back.
  const { setNoteTab } = micTest;
  useEffect(() => {
    setNoteTab('evidence');
    // Only on entry — `askEvidence` owns the tab while the user is here.
  }, [setNoteTab]);

  return (
    <TourStepShell
      screenName={ScreenName.OnboardingTourEvidence}
      title={tourStepsMessages.evidenceTitle}
      subtitle={tourStepsMessages.evidenceSubtitle}
      onContinue={onContinue}
      onSkip={onSkip}
      // The answer is grounded in the note, so asking before it lands has
      // nothing to cite — the same guard the detail screen uses. Once the note
      // exists, Continue only waits for a question to be asked, not for the
      // answer to finish writing: Skip is always live beside it, so blocking
      // longer just pushes impatient finishers into the skipped bucket.
      continueDisabled={isNoteGenerating || !micTest.editActivity.usedEvidence}
      continueHint={
        isNoteGenerating
          ? tourStepsMessages.noteGeneratingHint
          : tourStepsMessages.evidenceContinueHint
      }
    >
      <div className="flex flex-col gap-3">
        <LearnBasicsEvidenceQuestions
          questions={evidenceQuestions}
          askedIndex={micTest.evidenceRequested ? micTest.evidenceQueryIndex : null}
          onAsk={askEvidence}
          variant="card"
          disabled={isNoteGenerating}
        />
        {micTest.editActivity.usedEvidence && (
          <TypographyCaption className="flex items-center gap-1.5 text-accent-forest">
            <Check className="size-3.5" />
            <FormattedMessage {...tourStepsMessages.evidenceAsked} />
          </TypographyCaption>
        )}
      </div>
    </TourStepShell>
  );
}
